'use client';

import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface TrustCenterModalProps {
  isOpen: boolean;
  onClose: () => void;
  page: 'home' | 'terms' | 'privacy' | 'dpa';
  darkMode?: boolean;
}

const pageConfig = {
  home: {
    title: 'Trust Center',
    url: '/trust-center',
  },
  terms: {
    title: 'Terms of Use',
    url: '/trust-center/terms-of-service',
  },
  privacy: {
    title: 'Privacy Policy',
    url: '/trust-center/privacy-policy',
  },
  dpa: {
    title: 'Data Processing Agreement',
    url: '/trust-center/dpa',
  },
};

export default function TrustCenterModal({
  isOpen,
  onClose,
  page,
  darkMode = false,
}: TrustCenterModalProps) {
  const config = pageConfig[page];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`w-full max-w-5xl transform overflow-hidden rounded-lg shadow-xl transition-all ${
                  darkMode ? 'bg-gray-800' : 'bg-white'
                }`}
              >
                {/* Header */}
                <div
                  className={`flex items-center justify-between border-b px-6 py-4 ${
                    darkMode ? 'border-gray-700' : 'border-gray-200'
                  }`}
                >
                  <Dialog.Title
                    className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  >
                    Valiyou Trust Center
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className={`rounded-lg p-2 transition-colors ${
                      darkMode
                        ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Iframe Content */}
                <div className="relative" style={{ height: '70vh' }}>
                  <iframe
                    src={config.url}
                    className="h-full w-full border-0"
                    title={config.title}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
